import React, { useEffect, useState } from "react";
import { toAssetUrl } from "../api/api";
import { createCategory, updateCategory } from "../api/categoriesApi";
import "../styles/CategoryForm.css";

export default function CategoryForm({ open, mode, category, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [status, setStatus] = useState(true);
  const [sortOrder, setSortOrder] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset the form every time it's opened for a (possibly different) category.
  useEffect(() => {
    if (!open) return;
    setName(category?.name || "");
    setStatus(category ? Boolean(Number(category.status)) : true);
    setSortOrder(category?.sort_order ?? "");
    setImageFile(null);
    setPreview(toAssetUrl(category?.image) || "");
    setError("");
    setSaving(false);
  }, [open, category]);

  // Let Escape close it, like any well-behaved dialog.
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Category name is required.");
      return;
    }

    setSaving(true);
    try {
      if (mode === "edit") {
        await updateCategory(category.id, {
          name: name.trim(),
          status,
          sort_order: sortOrder === "" ? undefined : Number(sortOrder),
          imageFile,
        });
      } else {
        await createCategory({ name: name.trim(), status, imageFile });
      }
      onSaved();
    } catch (err) {
      setError(
        err?.response?.data?.message || err.message || "Could not save this category. Please try again."
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="catform-overlay" onClick={onClose}>
      <div className="catform-card" onClick={(e) => e.stopPropagation()}>
        <div className="catform-header">
          <h2 className="catform-heading">
            {mode === "edit" ? "Edit Category" : "New Category"}
          </h2>
          <button
            type="button"
            className="catform-close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form className="catform-body" onSubmit={handleSubmit}>
          {error && <div className="postform-error">{error}</div>}

          <div className="catform-image-row">
            <div className="postform-image-preview catform-image-preview">
              {preview ? (
                <img src={preview} alt="Preview" />
              ) : (
                <span className="postform-image-placeholder">No image</span>
              )}
            </div>
            <div className="catform-image-controls">
              <label className="postform-label" style={{ marginTop: 0 }}>
                Category Image
              </label>
              <input
                type="file"
                onChange={handleImageChange}
                className="postform-file"
                accept="image/*"
              />
              {mode === "edit" && (
                <p className="postform-hint">Leave empty to keep the current image.</p>
              )}
            </div>
          </div>

          <label className="postform-label">Name</label>
          <input
            className="postform-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Technology"
            autoFocus
          />

          {mode === "edit" && (
            <>
              <label className="postform-label">Sort order</label>
              <input
                type="number"
                className="postform-input"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                placeholder="Leave blank to keep current position"
              />
            </>
          )}

          <div className="postform-toggle-row">
            <span>Active</span>
            <label className="postform-switch">
              <input
                type="checkbox"
                checked={status}
                onChange={(e) => setStatus(e.target.checked)}
              />
              <span className="postform-switch-slider" />
            </label>
          </div>

          <div className="catform-actions">
            <button
              type="button"
              className="postform-btn postform-btn-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="postform-btn postform-btn-save" disabled={saving}>
              {saving ? "Saving..." : mode === "edit" ? "Save Changes" : "Create Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
